import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { motion } from "framer-motion";
import { LogOut, User, Sun, Moon, Crown, Settings as SettingsIcon, LayoutDashboard, Instagram, PlayCircle, Layers, HelpCircle, Mail } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { ProfileAvatar } from "@/pages/Settings";

// External community links — swap the URLs as your comms channels come online.
const SOCIAL_LINKS = {
  instagram: "https://instagram.com/bliencer",
  discord: "https://discord.gg/bliencer",
  x: "https://x.com/bliencer",
};

// Inline SVGs for X and Discord (lucide-react doesn't ship these). Size prop
// mirrors the other icons in the menu so they render at the same visual weight.
const XIcon = ({ size = 14, ...props }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size} {...props}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);
const DiscordIcon = ({ size = 14, ...props }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size} {...props}>
    <path d="M20.317 4.3698a19.7913 19.7913 0 0 0-4.8851-1.5152.0741.0741 0 0 0-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 0 0-.0785-.037 19.7363 19.7363 0 0 0-4.8852 1.515.0699.0699 0 0 0-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 0 0 .0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 0 0 .0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 0 0-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 0 1-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 0 1 .0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 0 1 .0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 0 1-.0066.1276 12.2986 12.2986 0 0 1-1.873.8914.0766.0766 0 0 0-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 0 0 .0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 0 0 .0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 0 0-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.157 2.419 0 1.3332-.9556 2.4189-2.157 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1569 2.419 0 1.3332-.9461 2.4189-2.1569 2.4189Z" />
  </svg>
);

export default function Nav() {
  const { user, logout } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const navigate = useNavigate();

  return (
    <header
      className="fixed top-3 sm:top-5 inset-x-3 sm:inset-x-6 z-50 pointer-events-none"
      data-testid="site-nav"
    >
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        className="mx-auto max-w-6xl pointer-events-auto"
      >
        <div className="liquid-glass relative h-14 grid grid-cols-[auto_1fr_auto] items-center gap-4 pl-6 pr-3 rounded-full">
          {/* Brand */}
          <Link to="/" data-testid="brand-link" className="flex items-center shrink-0 brand-liquid-glass">
            <img
              src="/brand/bliencer-mark.png"
              alt="Bliencer"
              data-testid="brand-logo-mark"
              className="sm:hidden h-5 w-auto select-none"
              draggable={false}
            />
            <img
              src="/brand/bliencer-wordmark.png"
              alt="Bliencer"
              data-testid="brand-logo-wordmark"
              className="hidden sm:block h-[18px] w-auto select-none"
              draggable={false}
            />
          </Link>

          {/* Center nav — hidden for logged-in users; those links move into the profile dropdown */}
          {!user && (
            <nav className="hidden md:flex items-center justify-center gap-6 lg:gap-7 text-sm text-[#a0a0a0] min-w-0">
              <a href="/#how" className="hover:text-white btn-anim whitespace-nowrap" data-testid="nav-how">
                How it works
              </a>
              <a href="/#services" className="hover:text-white btn-anim" data-testid="nav-services">
                Services
              </a>
              <a href="/#faq" className="hover:text-white btn-anim" data-testid="nav-faq">
                FAQ
              </a>
              <Link to="/contact" className="hover:text-white btn-anim" data-testid="nav-contact">
                Contact
              </Link>
            </nav>
          )}
          {user && <div />}

          {/* Right controls */}
          <div className="flex items-center gap-1.5 shrink-0 justify-self-end">
            <button
              data-testid="nav-theme-toggle"
              onClick={toggleTheme}
              aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              className="h-9 w-9 rounded-full flex items-center justify-center text-white hover:bg-white/10 btn-anim"
            >
              {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
            </button>

            {user ? (
              <>
                {/* Prime pill — members only */}
                <PrimePill user={user} />

                {/* Profile dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid="nav-profile-trigger"
                      aria-label="Account menu"
                      className="ml-1 h-9 w-9 rounded-full flex items-center justify-center hover:opacity-80 btn-anim outline-none focus:outline-none focus-visible:outline-none focus:ring-0 border-0"
                    >
                      <ProfileAvatar user={user} size={32} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    sideOffset={10}
                    className="w-64 bg-[#0d0d0d] border-[#2b2b2b] text-white"
                    data-testid="nav-profile-menu"
                  >
                    <DropdownMenuLabel className="py-3">
                      <div className="flex items-center gap-3">
                        <ProfileAvatar user={user} size={36} />
                        <div className="min-w-0">
                          <div className="text-sm font-semibold truncate">
                            {user.name || "Account"}
                          </div>
                          <div className="text-xs text-[#a0a0a0] truncate">
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-[#2b2b2b]" />
                    <DropdownMenuItem
                      onClick={() => navigate("/dashboard")}
                      data-testid="menu-dashboard"
                      className="cursor-pointer focus:bg-[#1a1a1a] focus:text-white"
                    >
                      <LayoutDashboard size={14} className="mr-2" /> Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate("/settings")}
                      data-testid="menu-settings"
                      className="cursor-pointer focus:bg-[#1a1a1a] focus:text-white"
                    >
                      <SettingsIcon size={14} className="mr-2" /> Account settings
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate("/prime")}
                      data-testid="menu-manage-plan"
                      className="cursor-pointer focus:bg-[#1a1a1a] focus:text-white"
                    >
                      <Crown size={14} className="mr-2" style={{ color: "#a855f7" }} /> Manage plan
                    </DropdownMenuItem>
                    {user.role === "admin" && (
                      <DropdownMenuItem
                        onClick={() => navigate("/admin")}
                        data-testid="menu-admin"
                        className="cursor-pointer focus:bg-[#1a1a1a] focus:text-white"
                      >
                        <User size={14} className="mr-2" /> Admin panel
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator className="bg-[#2b2b2b]" />
                    <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-[#7a7a7a] pt-2 pb-1">
                      Explore
                    </DropdownMenuLabel>
                    <DropdownMenuItem
                      onClick={() => { navigate("/"); setTimeout(() => { window.location.hash = "#how"; }, 50); }}
                      data-testid="menu-how"
                      className="cursor-pointer focus:bg-[#1a1a1a] focus:text-white"
                    >
                      <PlayCircle size={14} className="mr-2" /> How it works
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => { navigate("/"); setTimeout(() => { window.location.hash = "#services"; }, 50); }}
                      data-testid="menu-services"
                      className="cursor-pointer focus:bg-[#1a1a1a] focus:text-white"
                    >
                      <Layers size={14} className="mr-2" /> Services
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => { navigate("/"); setTimeout(() => { window.location.hash = "#faq"; }, 50); }}
                      data-testid="menu-faq"
                      className="cursor-pointer focus:bg-[#1a1a1a] focus:text-white"
                    >
                      <HelpCircle size={14} className="mr-2" /> FAQ
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate("/contact")}
                      data-testid="menu-contact"
                      className="cursor-pointer focus:bg-[#1a1a1a] focus:text-white"
                    >
                      <Mail size={14} className="mr-2" /> Contact
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-[#2b2b2b]" />
                    <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-[#7a7a7a] pt-2 pb-1">
                      Socials
                    </DropdownMenuLabel>
                    <DropdownMenuItem
                      onClick={() => window.open(SOCIAL_LINKS.instagram, "_blank", "noopener,noreferrer")}
                      data-testid="menu-instagram"
                      className="cursor-pointer focus:bg-[#1a1a1a] focus:text-white"
                    >
                      <Instagram size={14} className="mr-2" /> Instagram
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => window.open(SOCIAL_LINKS.discord, "_blank", "noopener,noreferrer")}
                      data-testid="menu-discord"
                      className="cursor-pointer focus:bg-[#1a1a1a] focus:text-white"
                    >
                      <DiscordIcon size={14} className="mr-2" /> Discord
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => window.open(SOCIAL_LINKS.x, "_blank", "noopener,noreferrer")}
                      data-testid="menu-x"
                      className="cursor-pointer focus:bg-[#1a1a1a] focus:text-white"
                    >
                      <XIcon size={14} className="mr-2" /> X
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-[#2b2b2b]" />
                    <DropdownMenuItem
                      data-testid="menu-logout"
                      onClick={async () => {
                        await logout();
                        navigate("/");
                      }}
                      className="cursor-pointer focus:bg-[#1a1a1a] focus:text-white"
                    >
                      <LogOut size={14} className="mr-2" /> Log out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  data-testid="nav-login"
                  className="hidden sm:inline-flex items-center h-9 px-3 rounded-full text-sm text-[#a0a0a0] hover:text-white hover:bg-white/10 btn-anim"
                >
                  Log in
                </Link>
                <Link
                  to="/signup"
                  data-testid="nav-signup-mobile"
                  className="sm:hidden btn-accent rounded-full h-9 px-4 text-sm font-medium btn-anim inline-flex items-center gap-2 whitespace-nowrap"
                >
                  <User size={14} /> Sign up
                </Link>
                <Link
                  to="/order"
                  data-testid="nav-cta"
                  className="hidden sm:inline-flex btn-accent rounded-full h-9 px-4 text-sm font-medium btn-anim items-center gap-2 whitespace-nowrap"
                >
                  <User size={14} /> Start Order
                </Link>
              </>
            )}
          </div>
        </div>
      </motion.div>
    </header>
  );
}

/** Prime pill — extracted so we can render it only for logged-in users.
    Label/badge reflect the user's real membership state: active members see
    their plan + true discount; everyone else sees a "Join" upsell. */
const PRIME_PILL_META = {
  prime: { label: "Pro", badge: "−15%" },
  prime_pro: { label: "Pro+", badge: "−25%" },
};

function PrimePill({ user }) {
  const m = user?.membership || {};
  const meta =
    (m.status === "active" && PRIME_PILL_META[m.plan]) || { label: "Pro", badge: "Join" };
  return (
    <Link
      to="/prime"
      data-testid="nav-prime"
      aria-label="Bliencer Pro membership"
      className="hidden sm:inline-flex items-center gap-2 h-8 pl-1 pr-1 rounded-full btn-anim group"
      style={{
        background: "linear-gradient(120deg, #c084fc 0%, #a855f7 45%, #8b5cf6 100%)",
        boxShadow:
          "0 0 0 1px rgba(255,255,255,0.22) inset, 0 2px 10px -4px rgba(168,85,247,0.32), 0 0 14px -6px rgba(139,92,246,0.28)",
      }}
    >
      <span
        className="h-6 w-6 rounded-full inline-flex items-center justify-center shrink-0"
        style={{
          background: "#1a0d2e",
          boxShadow: "0 0 0 1px rgba(216,180,254,0.22) inset",
        }}
      >
        <Crown size={11} color="#c084fc" style={{ filter: "drop-shadow(0 0 6px rgba(216,180,254,0.9))" }} />
      </span>
      <span
        className="text-[12.5px] font-semibold tracking-tight"
        style={{ letterSpacing: "-0.01em", color: "#1a0d2e" }}
      >
        {meta.label}
      </span>
      <span
        className="inline-flex items-center h-5 px-2 rounded-full text-[10px] font-bold uppercase tracking-wider"
        style={{ background: "#ffffff", color: "#1a0d2e", boxShadow: "0 1px 2px rgba(0,0,0,0.08)" }}
      >
        {meta.badge}
      </span>
    </Link>
  );
}

